import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, inputClass, primaryBtnClass, rigLabel } from "./serviceShared";
import { rosterIsSeedOnly } from "./serviceJobTypesConstants";
import type { useServiceWorkQueuesPanel } from "./useServiceWorkQueuesPanel";

export function ServiceJobTypesEditor(props: {
  m: ReturnType<typeof useServiceWorkQueuesPanel>;
  onSaved?: () => void;
}) {
  const { m, onSaved } = props;
  const showSeedExample = rosterIsSeedOnly(m.queues);

  return (
    <div className="space-y-4">
      {m.localError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      {showSeedExample ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Default job types are already seeded. Example:{" "}
          <strong>Internal upgrade → Spray foam rig, 4h default</strong> — warranty vs paid repair
          vs internal prep land on different board lanes.
        </p>
      ) : null}

      {!m.createDraftOpen ? (
        <button type="button" className={primaryBtnClass} onClick={m.startCreateDraft}>
          + Add job type
        </button>
      ) : (
        <SectionCard title="New job type">
          <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
            Name the lane and optional defaults — nothing is created until you click{" "}
            <strong>Create job type</strong>.
          </p>
          <JobTypeDraftFields m={m} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={primaryBtnClass}
              disabled={!m.isCreateDraftValid || m.actionBusy}
              onClick={() =>
                void m.saveQueue().then((ok) => {
                  if (ok) onSaved?.();
                })
              }
            >
              {m.actionBusy ? "Creating…" : "Create job type"}
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={m.actionBusy}
              onClick={m.cancelCreateDraft}
            >
              Cancel
            </button>
          </div>
        </SectionCard>
      )}

      <SectionCard title="Job type roster">
        {m.queues.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">No job types yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {m.queues.map((queue) => (
              <li key={queue.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                    {queue.label}{" "}
                    {!queue.is_active ? <span className="text-xs text-gray-500">(inactive)</span> : null}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-zinc-400">
                    {queue.slug} · {rigLabel(m.rigs, queue.default_rig_id)}
                    {queue.default_duration_min != null ? ` · ${queue.default_duration_min} min` : ""}{" "}
                    · approver {queue.default_approver_role}
                    {queue.auto_approve_manufacturing ? " · auto-approve mfg" : ""}
                    {queue.photos_required ? " · photos required" : ""}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-zinc-400">
                    <input
                      type="checkbox"
                      checked={queue.photos_required}
                      disabled={m.actionBusy}
                      onChange={(e) =>
                        void m.updateQueue(queue, { photos_required: e.target.checked })
                      }
                    />
                    Photos required
                  </label>
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={m.actionBusy}
                    onClick={() => void m.updateQueue(queue, { is_active: !queue.is_active })}
                  >
                    {queue.is_active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function JobTypeDraftFields(props: { m: ReturnType<typeof useServiceWorkQueuesPanel> }) {
  const { m } = props;
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <input
        className={inputClass}
        placeholder="Label (e.g. Pressure wash)"
        value={m.draft.label}
        onChange={(e) => m.setDraft((d) => ({ ...d, label: e.target.value }))}
      />
      <input
        className={inputClass}
        placeholder="Slug (optional — auto from label)"
        value={m.draft.slug}
        onChange={(e) => m.setDraft((d) => ({ ...d, slug: e.target.value }))}
      />
      <select
        className={inputClass}
        value={m.draft.default_rig_id}
        onChange={(e) => m.setDraft((d) => ({ ...d, default_rig_id: e.target.value }))}
      >
        <option value="">Default rig</option>
        {m.rigs.map((r) => (
          <option key={r.id} value={r.id}>
            {r.designation}
          </option>
        ))}
      </select>
      <input
        className={inputClass}
        type="number"
        min={0}
        placeholder="Default duration (minutes)"
        value={m.draft.default_duration_min}
        onChange={(e) => m.setDraft((d) => ({ ...d, default_duration_min: e.target.value }))}
      />
      <select
        className={inputClass}
        value={m.draft.default_approver_role}
        onChange={(e) => m.setDraft((d) => ({ ...d, default_approver_role: e.target.value }))}
      >
        {m.approverRoles.map((role) => (
          <option key={role} value={role}>
            Approver: {role}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
        <input
          type="checkbox"
          checked={m.draft.auto_approve_manufacturing}
          onChange={(e) =>
            m.setDraft((d) => ({ ...d, auto_approve_manufacturing: e.target.checked }))
          }
        />
        Auto-approve manufacturing auto-requests
      </label>
      <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={m.draft.photos_required}
          onChange={(e) => m.setDraft((d) => ({ ...d, photos_required: e.target.checked }))}
        />
        <span>
          Photos required before approval
          <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-zinc-400">
            Sales and fulfillment intake must attach at least one site photo before the request can
            be submitted for dispatcher review.
          </span>
        </span>
      </label>
    </div>
  );
}
